import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, RefreshCcw, Save } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as z from "zod";
import { ROUTES } from "@/constants/routes";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { DILEMMAS_STATUS } from "@/features/dilemmas/constants";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { useThemesSearch } from "@/features/themes/hooks/useThemesSearch";
import type { ApiTheme } from "@/features/themes/types";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { answersRepo, dilemmasRepo, questionsRepo } from "@/lib/repositories";
import { handleApiError } from "@/utils/errors";
import AiWritingAssistantAnswersSection, {
  type AiWritingAssistantAnswerErrors,
} from "./ai-writing-assistant-answers-section";
import { useRefineDilemma } from "../hooks/useRefineDilemma";

export interface AiWritingAssistantReviewAnswer {
  id: string;
  label: string;
  answer: string;
  feedback: string;
  isCorrect: boolean;
}

const TITLE_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 1000;
const ANSWER_MAX_LENGTH = 500;
const COMMENT_MAX_LENGTH = 2000;

interface AiWritingAssistantReviewViewProps {
  dilemma: ApiDilemma;
  onRefined: (dilemma: ApiDilemma) => void;
  onPublished: () => void;
  onCancel: () => void;
}

const AiWritingAssistantReviewView = ({
  dilemma,
  onRefined,
  onPublished,
  onCancel,
}: AiWritingAssistantReviewViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, setSearch, themes, isLoading } = useThemesSearch(
    dilemma.topic.name,
  );
  const { refine, isPending: isRefining } = useRefineDilemma();

  const question = dilemma.questions[0];

  const [selectedTheme, setSelectedTheme] = useState<ApiTheme | null>(
    dilemma.topic,
  );
  const [isPublishing, setIsPublishing] = useState(false);
  const [answers, setAnswers] = useState<AiWritingAssistantReviewAnswer[]>(() =>
    question.answers.map((answer) => ({
      id: answer.id,
      label: answer.label,
      answer: answer.payload,
      feedback: answer.comment,
      isCorrect: answer.is_correct,
    })),
  );
  const [answerErrors, setAnswerErrors] =
    useState<AiWritingAssistantAnswerErrors>({});
  const [globalAnswerError, setGlobalAnswerError] = useState("");

  const isBusy = isRefining || isPublishing;

  const reviewSchema = z.object({
    title: z
      .string()
      .min(1, t("modules.dilemmas.errors.title_required"))
      .max(
        TITLE_MAX_LENGTH,
        t("modules.dilemmas.errors.max_length", { max: TITLE_MAX_LENGTH }),
      ),
    description: z
      .string()
      .min(1, t("modules.dilemmas.errors.description_required"))
      .max(
        DESCRIPTION_MAX_LENGTH,
        t("modules.dilemmas.errors.max_length", {
          max: DESCRIPTION_MAX_LENGTH,
        }),
      ),
    question: z
      .string()
      .min(1, t("modules.dilemmas.errors.question_required"))
      .max(500, t("modules.dilemmas.errors.max_length", { max: 500 })),
  });

  const form = useForm<z.infer<typeof reviewSchema>>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      title: dilemma.name,
      description: dilemma.description,
      question: question.payload,
    },
  });

  function validateAnswers(): boolean {
    const errors: AiWritingAssistantAnswerErrors = {};
    for (const answer of answers) {
      const fieldErrors: { answer?: string; feedback?: string } = {};
      if (!answer.answer.trim()) {
        fieldErrors.answer = t("modules.dilemmas.errors.answer_required");
      } else if (answer.answer.length > ANSWER_MAX_LENGTH) {
        fieldErrors.answer = t("modules.dilemmas.errors.max_length", {
          max: ANSWER_MAX_LENGTH,
        });
      }
      if (!answer.feedback.trim()) {
        fieldErrors.feedback = t("modules.dilemmas.errors.feedback_required");
      } else if (answer.feedback.length > COMMENT_MAX_LENGTH) {
        fieldErrors.feedback = t("modules.dilemmas.errors.max_length", {
          max: COMMENT_MAX_LENGTH,
        });
      }
      if (Object.keys(fieldErrors).length > 0) {
        errors[answer.id] = fieldErrors;
      }
    }
    const hasNoCorrectAnswer = !answers.some((answer) => answer.isCorrect);
    setGlobalAnswerError(
      hasNoCorrectAnswer
        ? t("modules.dilemmas.errors.at_least_one_correct")
        : "",
    );
    setAnswerErrors(errors);
    return Object.keys(errors).length === 0 && !hasNoCorrectAnswer;
  }

  async function handleRefine() {
    if (!selectedTheme) {
      toast.error(t("modules.dilemmas.errors.topic_required"));
      return;
    }
    const name = form.getValues("title").trim();
    if (!name) {
      form.setError("title", {
        message: t("modules.dilemmas.errors.title_required"),
      });
      return;
    }

    const [error, refined] = await catchError(
      refine(dilemma.id, { topic_id: selectedTheme.id, name }),
    );
    if (error || !refined) {
      toast.error(
        error
          ? handleApiError(error).message
          : t("modules.dilemmas.ai_assistant.refine_error"),
      );
      return;
    }
    onRefined(refined);
  }

  async function publishDilemma(formValues: z.infer<typeof reviewSchema>) {
    setIsPublishing(true);

    const [questionError] = await catchError(
      questionsRepo.patch(question.id, { payload: formValues.question }),
    );
    if (questionError) {
      toast.error(handleApiError(questionError).message);
      setIsPublishing(false);
      return;
    }

    const answerPromises = answers.map((answer) =>
      answersRepo.patch(answer.id, {
        payload: answer.answer,
        is_correct: answer.isCorrect,
        comment: answer.feedback,
      }),
    );
    const [answersError] = await catchError(Promise.all(answerPromises));
    if (answersError) {
      toast.error(handleApiError(answersError).message);
      setIsPublishing(false);
      return;
    }

    const [statusError] = await catchError(
      dilemmasRepo.patch(
        dilemma.id,
        {
          name: formValues.title,
          description: formValues.description,
          status: DILEMMAS_STATUS.PUBLISHED,
        },
        { is_last_step: true },
      ),
    );
    if (statusError) {
      toast.error(handleApiError(statusError).message);
      setIsPublishing(false);
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
    toast.success(t("modules.dilemmas.ai_assistant.save_success"));
    setIsPublishing(false);
    onPublished();
    navigate(ROUTES.DILEMMA_DETAILS(dilemma.id));
  }

  function handlePublish() {
    if (!validateAnswers()) return;
    form.handleSubmit(
      (values) => publishDilemma(values),
      () => toast.error(t("modules.dilemmas.errors.form_incomplete")),
    )();
  }

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor="ai-assistant-review-topic" required>
          {t("modules.dilemmas.form.topic")}
        </FieldLabel>
        <Combobox
          items={themes}
          value={selectedTheme}
          onValueChange={setSelectedTheme}
          inputValue={search}
          onInputValueChange={setSearch}
          filter={null}
          itemToStringLabel={(theme: ApiTheme) => theme?.name ?? ""}
          isItemEqualToValue={(a: ApiTheme, b: ApiTheme) => a?.id === b?.id}
        >
          <ComboboxInput
            id="ai-assistant-review-topic"
            showClear
            placeholder={t(
              "modules.dilemmas.ai_assistant.theme_field_placeholder",
            )}
            className="h-10 overflow-hidden"
            disabled={isBusy}
          />
          <ComboboxContent className="pointer-events-auto">
            <ComboboxEmpty>
              {isLoading
                ? t("keys.loading")
                : t("modules.dilemmas.ai_assistant.no_theme_found")}
            </ComboboxEmpty>
            <ComboboxList>
              {(theme: ApiTheme) => (
                <ComboboxItem key={theme.id} value={theme}>
                  {theme.name}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </Field>

      <form id="ai-writing-assistant-review-form">
        <FieldGroup className="gap-4">
          <Controller
            name="title"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="ai-assistant-title" required>
                  {t("modules.dilemmas.form.title")}
                </FieldLabel>
                <Input
                  {...field}
                  id="ai-assistant-title"
                  aria-invalid={fieldState.invalid}
                  disabled={isBusy}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="ai-assistant-description" required>
                  {t("modules.dilemmas.form.description")}
                </FieldLabel>
                <Textarea
                  {...field}
                  id="ai-assistant-description"
                  rows={3}
                  aria-invalid={fieldState.invalid}
                  disabled={isBusy}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="question"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="ai-assistant-question" required>
                  {t("modules.dilemmas.form.the_question")}
                </FieldLabel>
                <Input
                  {...field}
                  id="ai-assistant-question"
                  aria-invalid={fieldState.invalid}
                  disabled={isBusy}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {globalAnswerError && (
            <p className="text-sm text-destructive">{globalAnswerError}</p>
          )}
          <AiWritingAssistantAnswersSection
            answers={answers}
            setAnswers={setAnswers}
            errors={answerErrors}
          />
        </FieldGroup>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button
          type="button"
          variant="ghost"
          className="border border-primary text-primary"
          disabled={isBusy}
          onClick={handleRefine}
        >
          {isRefining ? (
            <Loader className="animate-spin" />
          ) : (
            <RefreshCcw className="size-4" />
          )}
          {t("modules.dilemmas.ai_assistant.regenerate_button")}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            disabled={isBusy}
            onClick={onCancel}
          >
            {t("keys.cancel")}
          </Button>
          <Button type="button" disabled={isBusy} onClick={handlePublish}>
            {isPublishing ? (
              <Loader className="animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {t("keys.save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiWritingAssistantReviewView;
